import React, { useState, useCallback } from "react";
import {
  Modal,
  Fade,
  Backdrop,
  Paper,
  Typography,
  TextField,
  Grid,
  Chip,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import PublishIcon from "@material-ui/icons/Publish";
import Dropzone from "react-dropzone";
import moment from "moment";
import { firestore, storage } from "../../../../firebase/firebase";
import { useAuth } from "../../../../providers/AuthContext";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    outline: 0,
  },
  modalPaper: {
    padding: theme.spacing(3),
    minWidth: 320,
    maxWidth: 500,
    outline: "none",
    borderRadius: theme.shape.borderRadius,
  },
  fileInput: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    transition: "border-color 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const INITIAL_STATE = { text: "", files: [], saving: false, confirmation: false };

// Self-contained bug report modal. Keeping its own local state means typing
// re-renders only this small component instead of the entire SideDrawer.
const BugReportModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { currentUser } = useAuth();
  const [data, setData] = useState(INITIAL_STATE);

  const handleClose = useCallback(() => {
    setData(INITIAL_STATE);
    onClose();
  }, [onClose]);

  const handleSend = useCallback(async () => {
    setData((prev) => ({ ...prev, saving: true }));

    try {
      const storageRef = storage.ref();
      const docRef = firestore.collection("bugReports").doc();
      const urls = [];

      for (const file of data.files) {
        const path = storageRef.child(`BugReports/${docRef.id}/${file.name}`);
        await path.put(file);
        urls.push(await path.getDownloadURL());
      }

      await docRef.set({
        date: moment().valueOf(),
        description: data.text,
        docId: docRef.id,
        os: navigator.userAgent,
        photoUrls: urls,
        sandbox: process.env.NODE_ENV === "development",
        user: currentUser.uid,
        version: "N/A",
      });

      setData((prev) => ({ ...prev, confirmation: true }));
      setTimeout(() => {
        setData(INITIAL_STATE);
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error sending bug report:", error);
      setData((prev) => ({ ...prev, saving: false }));
    }
  }, [data.files, data.text, currentUser.uid, onClose]);

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <Paper className={classes.modalPaper}>
          <Typography variant="h6" gutterBottom>
            Report a Problem
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Describe the issue you're experiencing..."
            value={data.text}
            onChange={(e) => {
              const text = e.target.value;
              setData((prev) => ({ ...prev, text }));
            }}
            style={{ marginBottom: 16 }}
          />

          <Dropzone onDrop={(files) => setData((prev) => ({ ...prev, files: [...prev.files, ...files] }))}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()} className={classes.fileInput}>
                <input {...getInputProps()} />
                <PublishIcon style={{ marginRight: 8, opacity: 0.6 }} />
                <Typography variant="body2">Drop files or click to upload</Typography>
              </div>
            )}
          </Dropzone>

          {data.files.length > 0 && (
            <Grid container spacing={1} style={{ marginTop: 8 }}>
              {data.files.map((file, index) => (
                <Grid item key={index}>
                  <Chip
                    label={file.name}
                    onDelete={() => {
                      const newFiles = [...data.files];
                      newFiles.splice(index, 1);
                      setData((prev) => ({ ...prev, files: newFiles }));
                    }}
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          )}

          <Grid container justifyContent="flex-end" spacing={1} style={{ marginTop: 16 }}>
            <Grid item>
              <Button onClick={handleClose} disabled={data.saving}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSend}
                disabled={data.saving || !data.text}
                startIcon={data.confirmation ? <CheckIcon /> : null}
              >
                {data.confirmation ? "Sent!" : data.saving ? "Sending..." : "Send"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default BugReportModal;
